<template>
  <el-dialog
    v-model="visible"
    :width="computedWidth"
    :lock-scroll="lockScroll"
    :append-to-body="appendToBody"
    :show-close="showClose"
    :align-center="alignCenter"
    :close-on-click-modal="closeOnClickModal"
    :close-on-press-escape="closeOnPressEscape"
    :class="['confirm-modal-common', customClass, { 'confirm-modal-web': !isMobile }]"
    @close="handleClose"
  >
    <template #header>
      <div class="confirm-title w-full text-[18px] font-semibold text-center text-[#000000] mt-1">
        {{ title }}
      </div>
    </template>
    
    <div class="confirm-content text-center font-medium text-[#362F2F]">
      <slot>
        <span v-html="content"></span>
      </slot>
    </div>
    
    <template #footer>
      <div class="confirm-actions flex justify-center items-center gap-2 mx-auto" >
        <el-button 
          v-if="showCancelButton"
          @click="handleCancel" 
          class="cancel-btn flex-1 text-[#362F2F] rounded-[8px] border-none bg-[#F7F7F7] hover:text-[#362F2F] hover:bg-[#F7F7F7] hover:opacity-70"
          :class="cancelButtonClass"
        >
          {{ cancelText }}
        </el-button>
        <el-button 
          v-if="showConfirmButton"
          @click="handleConfirm" 
          type="primary"
          class="config-btn flex-1 text-[#FFFFFF] rounded-[8px] border-none md:hover:opacity-80"
          :class="confirmButtonClass"
        >
          {{ confirmText }}

     
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { ElDialog, ElButton } from 'element-plus'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: '确认删除'
  },
  content: {
    type: String,
    default: '记录删除后不可恢复，请慎重考虑'
  },
  width: {
    type: [String, Number],
    default: '85%'
  },
  webWidth: {
    type: [String, Number],
    default: ''
  },
  lockScroll: {
    type: Boolean,
    default: true
  },
  appendToBody: {
    type: Boolean,
    default: false
  },
  showClose: {
    type: Boolean,
    default: false
  },
  // 点击遮罩是否关闭，强制场景可设为 false
  closeOnClickModal: {
    type: Boolean,
    default: true
  },
  // 按 ESC 是否关闭，强制场景建议与 closeOnClickModal 一并设为 false
  closeOnPressEscape: {
    type: Boolean,
    default: true
  },
  alignCenter: {
    type: Boolean,
    default: true
  },
  customClass: {
    type: String,
    default: 'rounded-[8px] pb-4'
  },
  cancelText: {
    type: String,
    default: '取消'
  },
  confirmText: {
    type: String,
    default: '确定'
  },
  showCancelButton: {
    type: Boolean,
    default: true
  },
  showConfirmButton: {
    type: Boolean,
    default: true
  },
  cancelButtonClass: {
    type: String,
    default: ''
  },
  confirmButtonClass: {
    type: String,
    default: ''
  },

})


const emit = defineEmits(['update:modelValue', 'confirm', 'cancel', 'close'])

const visible = ref(false)



// 计算宽度，如果传入了webWidth且当前是web端，则使用webWidth，否则使用width
const computedWidth = computed(() => {
  // 判断是否为移动端设备
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  
  // 如果是web端且传入了webWidth，则使用webWidth
  if (!isMobile && props.webWidth) {
    return props.webWidth
  }
  
  // 否则使用原来的width
  return props.width
})

// 检测是否为移动端
const isMobile = computed(() => {
  // 通过User Agent检测
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /android|ipad|iphone|ipod|iemobile|wpdesktop/i.test(userAgent);
});

watch(
  () => props.modelValue,
  (newVal) => {
    visible.value = newVal
  },
  { immediate: true }
)


watch(
  () => visible.value,
  (newVal) => {
    emit('update:modelValue', newVal)
  }
)

const handleClose = () => {
  visible.value = false
  emit('close')

}

const handleCancel = () => {
  visible.value = false
  emit('cancel')

}

const handleConfirm = () => {
  emit('confirm')

}



onMounted(() => {

 
});

 onUnmounted(() => {
    
  })

</script>

<style lang="scss" scoped>
.config-btn{
  min-width: 142px;
  padding: 23px 60px;
  background: linear-gradient( 90deg, #5787FF 0%, #1858F7 100%);
}
.cancel-btn{
  min-width: 142px;
  padding: 23px 60px;
}
.confirm-content{
  margin-bottom: 18px;
  font-size: 16px;
}

@media (min-width: 769px) {
  :deep(.confirm-modal-web.el-dialog) {
    border-radius: 20px;
    box-shadow: 0 12px 32px rgba(6, 10, 38, 0.16);
    padding: 24px;
  }


  :deep(.confirm-modal-web .el-dialog__header) {
    margin: 0;
    padding: 0;
  }

  :deep(.confirm-modal-web .el-dialog__body) {
    padding: 12px 0 0;
  }

  :deep(.confirm-modal-web .el-dialog__footer) {
    padding: 0;
  }

  .confirm-title {
    margin-top: 0;
    text-align: left;
    color: #060A26;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.3;
  }

  .confirm-content {
    margin-bottom: 0;
    text-align: left;
    color: rgba(6, 10, 38, 0.6);
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
  }

  .confirm-actions {
    width: 100%;
    margin: 18px 0 0;
    justify-content: flex-end;
    gap: 8px;
  }

  .cancel-btn,
  .config-btn {
    min-width: 120px;
    height: 34px;
    padding: 0 18px;
    border-radius: 51px;
    font-size: 14px;
    font-weight: 500;
    flex: 0;
    margin-left: 0;
  }

  .cancel-btn {
    border: 1px solid rgba(6, 10, 38, 0.1);
    background: #fff;
    color: #060A26;
  }

  .config-btn {
    border: 1px solid #326CFF;
    background: linear-gradient( 90deg, #326CFF 0%, #618EFF 100%);
  }
}


@media (max-width: 768px) {
  .config-btn{
    min-width: 6.5rem;
    padding: 1.4rem 3rem;
    border-radius: 36px;
    margin-left: 0;
  }
  .cancel-btn{
    min-width: 6.5rem;
    padding: 1.4rem 3rem;
    border-radius: 36px;
    border: 1px solid rgba(6,10,38,0.1);
    background: transparent;
  }
  .confirm-content{
    margin-bottom: 6px;
    font-size: 14px;
  }
}
</style>
